import { Stack, Typography } from "@mui/material";
import { useMemo } from "react";
import { ValueDisplayCard } from "../../components/NumericMetricCard.component";
import {
  useMatchAvgDurationFromCreationTocompletion,
  useMatchAvgDurationFromFirstToLastEnigma,
  useMatchAvgDurationFromPurchaseToEnd,
  useMatchCompletionRate,
  useMatchCount,
  useMatchOpenRate,
  useMatchPlayRate,
} from "../../hooks/cityEscapeStats.hooks";
import { MatchFilter } from "../../libs/statsFilters";
import { formatSecondsToDDHHMMSS } from "../../libs/utils";
import { GlobalStatsSelection } from "../../types/stats";

export function CityEscapeOverview({
  selection,
  filters,
  pending,
}: {
  selection?: GlobalStatsSelection;
  filters?: MatchFilter;
  pending?: boolean;
}) {
  return (
    <Stack direction={"column"} spacing={2}>
      <Typography variant="h6" gutterBottom>
        Panoramica
      </Typography>
      <Stack direction="row" spacing={2}>
        <CityEscapeMatchCountCard
          selection={selection}
          filters={filters}
          pending={pending}
        />
        <CityEscapeMatchCompletionRateCard
          selection={selection}
          filters={filters}
          pending={pending}
        />
        <CityEscapeMatchOpenRateCard
          selection={selection}
          filters={filters}
          pending={pending}
        />
      </Stack>
      <Stack direction="row" spacing={2}>
        <CityEscapeAvgDurationFromPurchaseCard
          selection={selection}
          filters={filters}
          pending={pending}
        />
        <CityEscapeAvgDurationFromCreationCard
          selection={selection}
          filters={filters}
          pending={pending}
        />
        <CityEscapeAvgDurationBetweenEnigmasCard
          selection={selection}
          filters={filters}
          pending={pending}
        />
      </Stack>
    </Stack>
  );
}

export function CityEscapeMatchCountCard({
  selection,
  filters,
  pending,
}: {
  selection?: GlobalStatsSelection;
  filters?: MatchFilter;
  pending?: boolean;
}) {
  const matchCount = useMatchCount(
    {
      selection,
      filters,
      opts: { verbose: false },
    },
    {
      enabled: !pending,
    }
  );

  return (
    <ValueDisplayCard
      title="Partite Create"
      value={matchCount.data ?? 0}
      isLoading={matchCount.isLoading}
    />
  );
}

export function CityEscapeMatchPlayRateCard({
  selection,
  filters,
  pending,
}: {
  selection?: GlobalStatsSelection;
  filters?: MatchFilter;
  pending?: boolean;
}) {
  const playRate = useMatchPlayRate(
    { selection, filters },
    {
      enabled: !pending,
    }
  );

  return (
    <ValueDisplayCard
      title="Partite Giocate"
      value={`${playRate.data?.toFixed(2) ?? 0}%`}
      isLoading={playRate.isLoading}
    />
  );
}

export function CityEscapeMatchCompletionRateCard({
  selection,
  filters,
  pending,
}: {
  selection?: GlobalStatsSelection;
  filters?: MatchFilter;
  pending?: boolean;
}) {
  const completionRate = useMatchCompletionRate(
    { selection, filters },
    {
      enabled: !pending,
    }
  );

  return (
    <ValueDisplayCard
      title="Partite Terminate"
      value={`${completionRate.data?.toFixed(2) ?? 0}%`}
      isLoading={completionRate.isLoading}
    />
  );
}

export function CityEscapeMatchOpenRateCard({
  selection,
  filters,
  pending,
}: {
  selection?: GlobalStatsSelection;
  filters?: MatchFilter;
  pending?: boolean;
}) {
  const openRate = useMatchOpenRate(
    { selection, filters },
    {
      enabled: !pending,
    }
  );

  return (
    <ValueDisplayCard
      title="Partite Aperte"
      value={`${openRate.data?.toFixed(2) ?? 0}%`}
      isLoading={openRate.isLoading}
    />
  );
}

export function CityEscapeAvgDurationFromPurchaseCard({
  selection,
  filters,
  pending,
}: {
  selection?: GlobalStatsSelection;
  filters?: MatchFilter;
  pending?: boolean;
}) {
  const avgDuration = useMatchAvgDurationFromPurchaseToEnd(
    {
      selection,
      filters,
    },
    {
      enabled: !pending,
    }
  );

  const formattedTime = useMemo(() => {
    return avgDuration.data
      ? formatSecondsToDDHHMMSS(avgDuration.data / 1000, { first: 3 })
      : "--";
  }, [avgDuration.data]);

  return (
    <ValueDisplayCard
      title="Tempo medio da acquisto"
      value={formattedTime}
      isLoading={avgDuration.isLoading}
    />
  );
}

export function CityEscapeAvgDurationFromCreationCard({
  selection,
  filters,
  pending,
}: {
  selection?: GlobalStatsSelection;
  filters?: MatchFilter;
  pending?: boolean;
}) {
  const avgDuration = useMatchAvgDurationFromCreationTocompletion(
    {
      selection,
      filters,
    },
    {
      enabled: !pending,
    }
  );

  const formattedTime = useMemo(() => {
    return avgDuration.data
      ? formatSecondsToDDHHMMSS(avgDuration.data / 1000, { first: 3 })
      : "--";
  }, [avgDuration.data]);

  return (
    <ValueDisplayCard
      title="Tempo medio da creazione"
      value={formattedTime}
      isLoading={avgDuration.isLoading}
    />
  );
}

export function CityEscapeAvgDurationBetweenEnigmasCard({
  selection,
  filters,
  pending,
}: {
  selection?: GlobalStatsSelection;
  filters?: MatchFilter;
  pending?: boolean;
}) {
  const avgDuration = useMatchAvgDurationFromFirstToLastEnigma(
    {
      selection,
      filters,
    },
    {
      enabled: !pending,
    }
  );

  const formattedTime = useMemo(() => {
    return avgDuration.data
      ? formatSecondsToDDHHMMSS(avgDuration.data / 1000, { first: 3 })
      : "--";
  }, [avgDuration.data]);

  return (
    <ValueDisplayCard
      title="Tempo medio tra enigmi estremi"
      value={formattedTime}
      isLoading={avgDuration.isLoading}
    />
  );
}
